/**
 * ============================================================================
 *  File        : frontend/src/pages/CaseCreatePage.tsx
 *  Module      : pages
 *  Purpose     : /cases/new 新增案件（spec §8.2）— react-hook-form + zod + 動態品項
 *  Author      : EF Engineering
 *  Created     : 2026-05-31
 *  Updated     : 2026-05-31
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  欄位映射 → 後端 v13 CreateCaseDto：
 *    title（抬頭，可選；不填後端自動產生 case_no）+ items[]。
 *    每列 item 對應 CreateCaseItemDto：item_name / declared_weight_kg /
 *    min_unit_price / quantity / uom / packaging / bag_unit_weight_kg，
 *    重量・単価・数量以「字串」送出（rust_decimal，禁 float）。
 */

import type { ReactElement } from 'react'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { createCase, listMemberOrgs, type CreateCaseDto } from '../api/cases'
import { ApiError } from '../api/client'
import { EF260128_SAMPLE_ITEMS, EF260128_TITLE, emptyItemRow } from '../data/ef260128Sample'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { toast } from '../components/ui/sonner'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table'

interface PageProps {
  t: (key: string) => string
}

/** Decimal-as-string 欄位：非空 + 可轉正數。 */
const decimalString = z
  .string()
  .trim()
  .min(1)
  .refine((v) => Number.isFinite(Number(v)) && Number(v) > 0, { message: 'positive' })

const itemSchema = z.object({
  itemName: z.string().trim().min(1),
  declaredWeightKg: decimalString,
  minUnitPrice: decimalString,
  quantity: decimalString,
  uom: z.enum(['kg', 'pcs']),
  packaging: z.enum(['flexible', 'small_flexible', 'bara', 'pallet', 'other']),
  bagUnitWeightKg: z.string().trim().optional().default(''),
})

const formSchema = z.object({
  title: z.string().trim().optional().default(''),
  memberOrgId: z.string().optional().default(''),
  items: z.array(itemSchema).min(1),
})

type CaseFormValues = z.input<typeof formSchema>

const UOM_OPTIONS = [
  { value: 'kg', labelKey: 'option.unitKg' },
  { value: 'pcs', labelKey: 'option.unitPiece' },
] as const

const PACKAGING_OPTIONS = [
  { value: 'flexible', labelKey: 'option.packFlexible' },
  { value: 'small_flexible', labelKey: 'option.packSmallFlexible' },
  { value: 'bara', labelKey: 'option.packBara' },
  { value: 'pallet', labelKey: 'option.packPallet' },
  { value: 'other', labelKey: 'option.packOther' },
] as const

/** 表單值 → 後端 v13 DTO。bagUnitWeightKg 非空才送；memberOrgId 非空才送。 */
function toDto(values: CaseFormValues): CreateCaseDto {
  const memberOrgIdNum = values.memberOrgId ? Number(values.memberOrgId) : undefined
  return {
    title: values.title?.trim() || undefined,
    member_org_id: memberOrgIdNum && Number.isFinite(memberOrgIdNum) ? memberOrgIdNum : undefined,
    items: values.items.map((item) => ({
      item_name: item.itemName.trim(),
      declared_weight_kg: item.declaredWeightKg.trim(),
      min_unit_price: item.minUnitPrice.trim(),
      quantity: item.quantity.trim(),
      uom: item.uom,
      packaging: item.packaging,
      bag_unit_weight_kg: item.bagUnitWeightKg?.trim() || undefined,
    })),
  }
}

export function CaseCreatePage({ t }: PageProps): ReactElement {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: memberOrgs = [] } = useQuery({
    queryKey: ['member-orgs'],
    queryFn: listMemberOrgs,
  })

  const form = useForm<CaseFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      memberOrgId: '',
      items: [emptyItemRow()],
    },
  })

  const { fields, append, remove, replace } = useFieldArray({
    control: form.control,
    name: 'items',
  })

  const mutation = useMutation({
    mutationFn: (values: CaseFormValues) => createCase(toDto(values)),
    onSuccess: (detail) => {
      toast.success(t('case.new.success'))
      void queryClient.invalidateQueries({ queryKey: ['cases'] })
      void navigate(`/cases/${detail.id}`)
    },
    onError: (error) => {
      const detail = error instanceof ApiError ? `（${error.message}）` : ''
      toast.error(`${t('case.new.error')}${detail}`)
    },
  })

  const onSubmit = form.handleSubmit((values) => mutation.mutate(values))

  function loadSample(): void {
    form.setValue('title', EF260128_TITLE)
    replace(EF260128_SAMPLE_ITEMS)
  }

  return (
    <section className="page-stack">
      <header className="page-heading flex items-start justify-between gap-4">
        <div>
          <span className="eyebrow">{t('nav.cases')}</span>
          <h1>{t('case.new.title')}</h1>
          <p>{t('case.new.subtitle')}</p>
        </div>
        {import.meta.env.DEV || import.meta.env.VITE_E2E === '1' ? (
          <Button
            type="button"
            variant="outline"
            data-testid="load-ef260128-sample"
            onClick={loadSample}
          >
            {t('case.new.loadSample')}
          </Button>
        ) : null}
      </header>

      <form className="space-y-6" onSubmit={(e) => void onSubmit(e)}>
        <fieldset className="space-y-4 rounded-md border border-border p-4">
          <legend className="px-1 text-sm font-medium">{t('case.new.headerSection')}</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="title">{t('field.caseTitle')}</Label>
              <Input id="title" placeholder={t('case.new.titlePlaceholder')} {...form.register('title')} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="member-org-select">{t('case.new.memberOrgLabel')}</Label>
              <Controller
                control={form.control}
                name="memberOrgId"
                render={({ field: ctrl }) => (
                  <Select value={ctrl.value ?? ''} onValueChange={ctrl.onChange}>
                    <SelectTrigger id="member-org-select" data-testid="member-org-select">
                      <SelectValue placeholder={t('case.new.memberOrgPlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {memberOrgs.map((org) => (
                        <SelectItem key={org.id} value={String(org.id)}>
                          {org.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>
        </fieldset>

        <fieldset className="space-y-3 rounded-md border border-border p-4">
          <legend className="px-1 text-sm font-medium">{t('case.new.itemsSection')}</legend>
          <div className="overflow-x-auto rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[10rem]">{t('field.itemName')}</TableHead>
                  <TableHead className="min-w-[7rem]">{t('field.declaredWeight')}</TableHead>
                  <TableHead className="min-w-[8rem]">{t('field.minUnitPrice')}</TableHead>
                  <TableHead className="min-w-[6rem]">{t('field.quantityCount')}</TableHead>
                  <TableHead className="min-w-[6rem]">{t('field.uom')}</TableHead>
                  <TableHead className="min-w-[8rem]">{t('field.packageStyle')}</TableHead>
                  <TableHead className="min-w-[6rem]">{t('field.bagUnitWeight')}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {fields.map((field, index) => {
                  const itemErrors = form.formState.errors.items?.[index]
                  return (
                    <TableRow key={field.id}>
                      <TableCell>
                        <Input
                          aria-invalid={!!itemErrors?.itemName}
                          {...form.register(`items.${index}.itemName`)}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          inputMode="decimal"
                          aria-invalid={!!itemErrors?.declaredWeightKg}
                          {...form.register(`items.${index}.declaredWeightKg`)}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          inputMode="decimal"
                          aria-invalid={!!itemErrors?.minUnitPrice}
                          {...form.register(`items.${index}.minUnitPrice`)}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          inputMode="decimal"
                          aria-invalid={!!itemErrors?.quantity}
                          {...form.register(`items.${index}.quantity`)}
                        />
                      </TableCell>
                      <TableCell>
                        <Controller
                          control={form.control}
                          name={`items.${index}.uom`}
                          render={({ field: ctrl }) => (
                            <Select value={ctrl.value} onValueChange={ctrl.onChange}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {UOM_OPTIONS.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {t(opt.labelKey)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <Controller
                          control={form.control}
                          name={`items.${index}.packaging`}
                          render={({ field: ctrl }) => (
                            <Select value={ctrl.value} onValueChange={ctrl.onChange}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {PACKAGING_OPTIONS.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {t(opt.labelKey)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          inputMode="decimal"
                          {...form.register(`items.${index}.bagUnitWeightKg`)}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={fields.length <= 1}
                          onClick={() => remove(index)}
                        >
                          {t('case.new.removeItem')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
          {form.formState.errors.items && !Array.isArray(form.formState.errors.items) ? (
            <p className="text-xs text-destructive">{t('case.new.itemsRequired')}</p>
          ) : null}
          <Button type="button" variant="outline" size="sm" onClick={() => append(emptyItemRow())}>
            {t('case.new.addItem')}
          </Button>
        </fieldset>

        <div className="flex items-center gap-2">
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? t('case.new.submitting') : t('case.new.submit')}
          </Button>
          <Button type="button" variant="ghost" onClick={() => void navigate('/cases')}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </section>
  )
}
